import React, { useState, useEffect } from "react";
import { 
  TextField, 
  Button, 
  Box, 
  Typography, 
  MenuItem, 
  CircularProgress,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormControl,
  FormLabel
} from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import { createDamageReportAction, clearCreateDamageReportAction, getRawMaterialsAction } from "../../store/actions";
import toast from "react-hot-toast";

const ShortageDamage = () => {
  const dispatch = useDispatch();
  const [selectedFile, setSelectedFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  
  const { loading: createLoading, error: createError, successData: createSuccess } = useSelector(
    (state) => state.createDamageReportReducer
  );
  
  const { successData: rawMaterials } = useSelector((state) => state.getRawMaterialsReducer);
  const userDetails = JSON.parse(localStorage.getItem('userDetails') || '{}');
  const userId = userDetails.user_id || '';


  useEffect(() => {
    dispatch(getRawMaterialsAction());
    
    return () => {
      dispatch(clearCreateDamageReportAction());
    };
  }, [dispatch]);

  useEffect(() => {
    if (createSuccess) {
      toast.success("Report created successfully");
      formik.resetForm();
      setSelectedFile(null);
      setImagePreview(null);
      dispatch(clearCreateDamageReportAction());
    }
    
    if (createError) {
      toast.error(createError || "Failed to create report");
      dispatch(clearCreateDamageReportAction());
    }
  }, [createSuccess, createError, dispatch]);

  const validationSchema = Yup.object({
    raw_material_id: Yup.string().required("Raw material is required"),
    quantity_damaged: Yup.number()
      .required("Quantity is required")
      .positive("Quantity must be positive")
      .integer("Quantity must be an integer"),
    reason: Yup.string().required("Reason is required").min(10, "Reason should be at least 10 characters"),
    type: Yup.string().required("Type is required").oneOf(['Damage', 'Shortage'], "Invalid type")
  });

  const formik = useFormik({
    initialValues: {
      raw_material_id: "",
      quantity_damaged: "",
      reason: "",
      image: null,
      type: "Damage" // Default value
    },
    validationSchema,
    onSubmit: (values) => {
      const formData = new FormData();
      formData.append("raw_material_id", values.raw_material_id);
      formData.append("quantity_damaged", parseInt(values.quantity_damaged));
      formData.append("reason", values.reason);
      formData.append("reported_by", userId || "");
      formData.append("type", values.type);
      
      // Add image to formData if it exists
      if (selectedFile) {
        formData.append("image", selectedFile);
      }
      
      dispatch(createDamageReportAction(formData));
    }
  });

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
      formik.setFieldValue("image", file);
      
      // Create image preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  return (
    <Box
      sx={{
        maxWidth: 500,
        mx: "auto",
        mt: { xs: 12, sm: 16, md: 15 },
        pl: { xs: 2, sm: 2, md:25 },
        pr: { xs: 2, sm: 'auto'},
      }}
    >
      <Typography variant="h5" sx={{ mb: 3, fontWeight: "bold" }}>
        Report Shortage/Damage
      </Typography>
      
      <form onSubmit={formik.handleSubmit}>
        <FormControl component="fieldset" sx={{ mb: 2 }}>
          <FormLabel component="legend">Report Type</FormLabel>
          <RadioGroup
            row
            name="type"
            value={formik.values.type}
            onChange={formik.handleChange}
          >
            <FormControlLabel value="Damage" control={<Radio />} label="Damage" />
            <FormControlLabel value="Shortage" control={<Radio />} label="Shortage" />
          </RadioGroup>
        </FormControl>
        
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            gap: 2,
          }}
        >
          <TextField
            select
            label="Raw Material"
            fullWidth
            variant="outlined"
            name="raw_material_id"
            value={formik.values.raw_material_id}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.raw_material_id && Boolean(formik.errors.raw_material_id)}
            helperText={formik.touched.raw_material_id && formik.errors.raw_material_id}
          >
            {rawMaterials?.data.map((material) => (
              <MenuItem key={material.id} value={material.id}>
                {material.name}
              </MenuItem>
            ))}
          </TextField>
          
          <TextField
            label={formik.values.type === "Damage" ? "Quantity Damaged" : "Quantity Short"}
            type="number"
            fullWidth
            variant="outlined"
            name="quantity_damaged"
            value={formik.values.quantity_damaged}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.quantity_damaged && Boolean(formik.errors.quantity_damaged)}
            helperText={formik.touched.quantity_damaged && formik.errors.quantity_damaged}
          />
        </Box>

        <TextField
          label="Reason"
          multiline
          rows={4}
          fullWidth
          variant="outlined"
          sx={{ mt: 2 }}
          name="reason"
          value={formik.values.reason}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.touched.reason && Boolean(formik.errors.reason)}
          helperText={formik.touched.reason && formik.errors.reason}
        />

        <Typography sx={{ mt: 2, mb: 1, fontWeight: "bold", color: "black" }}>
          Image (Optional)
        </Typography>

        <input
          type="file"
          accept="image/*"
          id="upload-button"
          style={{ display: "none" }}
          onChange={handleFileChange}
        />
        <label htmlFor="upload-button">
          <Button variant="outlined" component="span">
            Choose File
          </Button>
        </label>
        
        <Typography sx={{ mt: 1, color: selectedFile ? "black" : "text.secondary" }}>
          {selectedFile ? selectedFile.name : "No File Chosen"}
        </Typography>

        {imagePreview && (
          <Box sx={{ mt: 2, mb: 2 }}>
            <img 
              src={imagePreview} 
              alt="Preview" 
              style={{ maxWidth: "100%", maxHeight: "200px", objectFit: "contain" }} 
            />
          </Box>
        )}

        <Button
          variant="contained"
          fullWidth
          type="submit"
          disabled={createLoading}
          sx={{
            mt: 3,
            bgcolor: "primary.main",
            color: "white",
            "&:hover": { bgcolor: "primary" },
          }}
        >
          {createLoading ? <CircularProgress size={24} color="inherit" /> : "Submit"}
        </Button>
      </form>
    </Box>
  );
};

export default ShortageDamage;
